import {skipToken} from '@reduxjs/toolkit/query';
import {Card, DataStatus} from 'platform/components';
import {Box, Heading, Text, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {useEffect, useState} from 'react';

import {isNil, isNotEmpty, isNotNil, trim} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../i18n/i18n';
import {useGetBatchQuery, useListBatchVehiclesQuery} from '../../../store/importApi';
import {mapVehicleItemToImportVehicle} from '../utils/mapVehicleItemToImportVehicle';
import {ImportPricingTableHeader} from './ImportPricingTableHeader';
import {VehiclePricingResultRow} from './VehiclePricingResultRow';

const BATCH_POLLING_INTERVAL_MS = 10_000;
const VEHICLES_PAGE_LIMIT = 1000;

interface ImportPricingViewProps extends TestIdProps {
  batchId: string;
}

export function ImportPricingView(props: ImportPricingViewProps) {
  const [pollingInterval, setPollingInterval] = useState(BATCH_POLLING_INTERVAL_MS);

  const {
    data: batch,
    isError: isBatchError,
    isLoading: isBatchLoading,
  } = useGetBatchQuery({batchId: props.batchId}, {pollingInterval, skipPollingIfUnfocused: true});

  const vehiclesPolling =
    isNotNil(batch) && batch.status === 'pricing' ? BATCH_POLLING_INTERVAL_MS : 0;

  const vehiclesQueryArg = isNotNil(props.batchId)
    ? {batchId: props.batchId, page: 1, limit: VEHICLES_PAGE_LIMIT}
    : skipToken;

  const {
    data: vehiclesResponse,
    isLoading: isVehiclesLoading,
    isError: isVehiclesError,
  } = useListBatchVehiclesQuery(vehiclesQueryArg, {
    skip: isNil(batch),
    pollingInterval: vehiclesPolling,
    skipPollingIfUnfocused: true,
  });

  useEffect(() => {
    if (isBatchError || batch?.status === 'done') {
      setPollingInterval(0);
    }
  }, [batch?.status, isBatchError]);

  const statusMessage = match<[boolean, string | undefined], string>([isBatchError, batch?.status])
    .with([true, Pattern.any], () => i18n.t('page.import.pricing.error'))
    .with([false, 'pricing'], () => i18n.t('page.import.pricing.inProgress'))
    .with([false, 'done'], () => i18n.t('page.import.pricing.done'))
    .otherwise(() => i18n.t('page.import.pricing.inProgress'));

  const hasBatchCurrency = isNotNil(batch?.currency) && isNotEmpty(trim(batch.currency));
  const isCurrencyMissingError =
    !isBatchLoading && !isBatchError && isNotNil(batch) && !hasBatchCurrency;

  const isLoading = isBatchLoading || isVehiclesLoading;
  const isError = isBatchError || isVehiclesError || isCurrencyMissingError;

  const errorMessage = match<[boolean, boolean, boolean], string>([
    isBatchError,
    isVehiclesError,
    isCurrencyMissingError,
  ])
    .with([true, Pattern.any, Pattern.any], () => i18n.t('page.import.pricing.error'))
    .with([false, true, Pattern.any], () => i18n.t('page.import.pricing.vehiclesLoadError'))
    .with([false, false, true], () => i18n.t('page.import.pricing.currencyMissing'))
    .otherwise(() => i18n.t('page.import.pricing.vehiclesLoadError'));

  const vehicles = vehiclesResponse?.data ?? [];

  return (
    <Box width="100%" data-testid={suffixTestId('wrapper', props)}>
      <Card data-testid={suffixTestId('card', props)}>
        <Box padding={6}>
          <VStack spacing={4}>
            <Heading size={1}>{i18n.t('page.import.labels.stepVehiclePricing')}</Heading>
            <Text size="base" color="secondary">
              {statusMessage}
            </Text>
            <DataStatus
              isLoading={isLoading}
              isError={isError}
              errorMessage={errorMessage}
              isEmpty={vehicles.length === 0}
              minHeight={20}
              data-testid={suffixTestId('dataStatus', props)}
            >
              <VStack spacing={0}>
                {vehicles.length > 0 && (
                  <ImportPricingTableHeader
                    data-testid={suffixTestId('pricingTableHeader', props)}
                  />
                )}
                {vehicles.map((vehicleItem) => (
                  <VehiclePricingResultRow
                    key={vehicleItem.id}
                    vehicleItem={vehicleItem}
                    importVehicle={mapVehicleItemToImportVehicle(vehicleItem)}
                    data-testid={suffixTestId(`pricingRow-${vehicleItem.id}`, props)}
                  />
                ))}
              </VStack>
            </DataStatus>
          </VStack>
        </Box>
      </Card>
    </Box>
  );
}
